import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Plus, RefreshCw, Send, Trash2 } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { useConfirmar } from "@/componentes/ui/Confirmar"
import { comoFechaHora } from "@/lib/fechas"

type Grupo = {
  id: number
  nombre: string
  enlazado: boolean
  titulo_chat: string | null
  tipos: string[]
  activo: boolean
  codigo: string | null
  codigo_expira_en: string | null
  enlace: string
}

/** Lo que puede recibir un grupo, en palabras de quien lo elige. */
export const TIPOS_GRUPO: readonly (readonly [string, string])[] = [
  ["ORDEN_PAGO", "Órdenes de pago: en aprobación, aprobada, pagada y rechazada"],
  ["ORDENES_GENERADAS", "Órdenes generadas en lote: gastos programados y nómina"],
  ["EXPEDIENTE_ETA", "Expedientes del 2.0: ETA de la semana (7:00) y cuando se actualiza"],
  ["EXPEDIENTE_DAI", "Expedientes del 2.0: DAI por renovar (7:00) y cuando se registra o renueva"],
  ["EXPEDIENTE_ABIERTOS", "Expedientes del 2.0: los abiertos por estatus, en el parte de las 7:00"],
  [
    "EXPEDIENTE_ESTATUS",
    "Expedientes del 2.0: cada cambio de estatus (cada 15 min), con quién y a qué hora",
  ],
  ["PRESUPUESTO", "Presupuesto: partidas que llegan a su umbral o se pasan del tope"],
  ["SISTEMA2_CXC", "Cuentas por cobrar del 2.0: abiertas (7:05), con enlace al reporte en PDF"],
  ["SISTEMA2_CXP", "Cuentas por pagar del 2.0: abiertas (7:05), con enlace al reporte en PDF"],
  [
    "GASTOS_SEMANA",
    "Pagos programados de la semana: gastos y nómina de lunes a domingo (lunes 8:00, con las órdenes por pagar y la disponibilidad), con reporte en PDF",
  ],
  [
    "DISPONIBILIDAD_BANCARIA",
    "Disponibilidad bancaria: el saldo de cada cuenta (lunes a viernes 7:15), con reporte en PDF",
  ],
  ["CXC", "Cuentas por cobrar: cada cuenta por cobrar que se crea, con su cliente y su expediente"],
  [
    "CXP",
    "Cuentas por pagar: cada CxP del 2.0 que nace administrativa (expediente, servicio propio) o de flete",
  ],
  [
    "INGRESO",
    "Reporte de ingresos: cada ingreso reportado en Tesorería, con su pagador o por determinar",
  ],
  [
    "RESUMEN_MACRO",
    "Resumen macro: la foto del alcance de la disponibilidad (jueves 17:00), con reporte en PDF",
  ],
  [
    "GERENCIAL_SEMANA",
    "Resumen gerencial del viernes: la semana, sus movimientos y ajustes, y cómo va el mes (viernes 17:00), con PDF",
  ],
  [
    "SISTEMA2_PAGOS_OPERATIVOS",
    "Pagos operativos del 2.0: lo programado de la semana, lo atrasado y los fondos por pedir a cada cliente (lunes 7:30)",
  ],
  [
    "FACTURACION_SEMANA",
    "Facturación de la semana: lo que se le factura a cada cliente (viernes 16:00), con PDF",
  ],
  [
    "CUMPLIMIENTO",
    "Entes y parafiscales: solvencias y declaraciones por vencer (30, 15 y 5 días) y vencidas",
  ],
  ["TAREA_ASIGNADA", "Tareas asignadas"],
  ["TAREA_ESTADO", "Cambios de estado de las tareas"],
  ["TAREA_CAMBIO", "Cambios en el encargo de una tarea"],
  ["TAREA_AVANCE", "Avances de las tareas"],
  ["TAREA_COMENTARIO", "Comentarios en las tareas"],
  ["TAREA_VENCE", "Vencimientos de tareas"],
  ["CONTRATO_VENCE", "Contratos por vencer"],
  ["REPORTE_NUEVO", "Fallas del sistema reportadas"],
  ["RESUMEN_PENDIENTES", "Resumen de pendientes"],
]

/**
 * Las áreas en que se agrupan los avisos de un grupo (23/10): con ~30 casillas seguidas por
 * grupo, la página era una lista infinita. Lo que no esté aquí cae en «Otros».
 */
const AREAS: readonly (readonly [string, readonly string[]])[] = [
  [
    "Órdenes, gastos y presupuesto",
    ["ORDEN_PAGO", "ORDENES_GENERADAS", "GASTOS_SEMANA", "GERENCIAL_SEMANA", "PRESUPUESTO"],
  ],
  [
    "Tesorería y cuentas",
    [
      "DISPONIBILIDAD_BANCARIA",
      "INGRESO",
      "CXC",
      "CXP",
      "SISTEMA2_CXC",
      "SISTEMA2_CXP",
      "SISTEMA2_PAGOS_OPERATIVOS",
      "RESUMEN_MACRO",
      "FACTURACION_SEMANA",
    ],
  ],
  [
    "Expedientes del 2.0",
    ["EXPEDIENTE_ETA", "EXPEDIENTE_DAI", "EXPEDIENTE_ABIERTOS", "EXPEDIENTE_ESTATUS"],
  ],
  ["Talento y cumplimiento", ["CONTRATO_VENCE", "CUMPLIMIENTO"]],
  [
    "Tareas y sistema",
    [
      "TAREA_ASIGNADA",
      "TAREA_ESTADO",
      "TAREA_CAMBIO",
      "TAREA_AVANCE",
      "TAREA_COMENTARIO",
      "TAREA_VENCE",
      "REPORTE_NUEVO",
      "RESUMEN_PENDIENTES",
    ],
  ],
]

function porArea(): [string, (readonly [string, string])[]][] {
  const conocidos = new Set(AREAS.flatMap(([, t]) => t))
  const salida: [string, (readonly [string, string])[]][] = AREAS.map(([area, tipos]) => [
    area,
    TIPOS_GRUPO.filter(([t]) => tipos.includes(t)),
  ])
  const otros = TIPOS_GRUPO.filter(([t]) => !conocidos.has(t))
  if (otros.length > 0) salida.push(["Otros", otros])
  return salida.filter(([, t]) => t.length > 0)
}

/**
 * Los grupos de Telegram que reciben avisos: la bitácora del equipo.
 *
 * ⚠ **Un grupo recibe el HECHO**, también lo que hizo uno mismo: es el registro de lo
 * que pasó, no un recordatorio para alguien (ver `services/avisos.py`).
 *
 * ⚠ **Se enlaza desde dentro del grupo**, con un código de quince minutos. El enlace
 * `t.me/<bot>?startgroup=…` hace las tres cosas de un toque: elegir el grupo, añadir
 * el bot y mandarle el código.
 *
 * ⚠ **Maestro–detalle** (23/10): a la izquierda la lista compacta, con buscador; a la
 * derecha SÓLO el grupo elegido, con sus avisos agrupados por área y plegables. Antes cada
 * grupo pintaba sus ~30 casillas una tras otra y la página no terminaba nunca.
 */
export function GruposTelegram() {
  const clienteQuery = useQueryClient()
  const [confirmacion, confirmar] = useConfirmar()
  const [nombre, setNombre] = useState("")
  const [buscar, setBuscar] = useState("")
  const [elegidoId, setElegidoId] = useState<number | null>(null)
  const grupos = useQuery({
    queryKey: ["telegram", "grupos"],
    queryFn: () => api.get<Grupo[]>("/api/v1/telegram/grupos"),
    // Mientras haya un código vivo se pregunta seguido: el enlace lo confirma el bot.
    refetchInterval: (c) => ((c.state.data ?? []).some((g) => g.codigo) ? 4_000 : false),
  })
  const refrescar = () => clienteQuery.invalidateQueries({ queryKey: ["telegram", "grupos"] })

  const crear = useMutation({
    mutationFn: () => api.post<Grupo>("/api/v1/telegram/grupos", { nombre, tipos: ["ORDEN_PAGO"] }),
    onSuccess: (g) => {
      setNombre("")
      if (g?.id) setElegidoId(g.id)
      refrescar()
    },
  })
  const editar = useMutation({
    mutationFn: (c: { id: number; tipos?: string[]; activo?: boolean }) =>
      api.patch<Grupo>(`/api/v1/telegram/grupos/${c.id}`, c),
    onSuccess: refrescar,
  })
  const codigo = useMutation({
    mutationFn: (id: number) => api.post<Grupo>(`/api/v1/telegram/grupos/${id}/codigo`),
    onSuccess: refrescar,
  })
  const borrar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/v1/telegram/grupos/${id}`),
    onSuccess: () => {
      setElegidoId(null)
      refrescar()
    },
  })
  const fallo = crear.error ?? editar.error ?? codigo.error ?? borrar.error

  const todos = grupos.data ?? []
  const q = buscar.trim().toLowerCase()
  const visibles = todos.filter(
    (g) => q === "" || `${g.nombre} ${g.titulo_chat ?? ""}`.toLowerCase().includes(q),
  )
  const g = todos.find((x) => x.id === elegidoId) ?? visibles[0] ?? null
  const areas = porArea()

  return (
    <section aria-label="Grupos de Telegram" className="space-y-4">
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Un grupo recibe los avisos que elijas, también los de lo que haces tú: sirve de bitácora del
        equipo.
      </p>

      {grupos.isPending && (
        <p role="status" className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando…
        </p>
      )}
      {grupos.error && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {grupos.error.message}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
        {/* La lista: compacta, con buscador y el alta debajo. */}
        <div className="space-y-3">
          {todos.length > 5 && (
            <input
              className="campo h-9 w-full text-sm"
              placeholder="Buscar grupo…"
              aria-label="Buscar grupo"
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
            />
          )}
          {todos.length === 0 && !grupos.isPending && (
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              Todavía no hay ningún grupo.
            </p>
          )}
          <ul
            className="tarjeta max-h-[28rem] divide-y overflow-y-auto"
            style={{ borderColor: "var(--border)" }}
          >
            {visibles.map((x) => (
              <li key={x.id}>
                <button
                  type="button"
                  aria-current={g?.id === x.id ? "true" : undefined}
                  onClick={() => setElegidoId(x.id)}
                  className={`foco-cristal w-full p-3 text-left text-sm ${g?.id === x.id ? "activo-cristal" : "toque-cristal"}`}
                >
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="size-2 shrink-0 rounded-full"
                      style={{
                        background: !x.activo
                          ? "var(--fg-muted)"
                          : x.enlazado
                            ? "var(--success)"
                            : "var(--warning)",
                      }}
                    />
                    <span className="min-w-0 flex-1 truncate font-medium">{x.nombre}</span>
                    <span
                      className="shrink-0 text-xs tabular-nums"
                      style={{ color: "var(--fg-muted)" }}
                    >
                      {x.tipos.length} aviso{x.tipos.length === 1 ? "" : "s"}
                    </span>
                  </span>
                  <span
                    className="mt-0.5 block truncate text-xs"
                    style={{ color: "var(--fg-muted)" }}
                  >
                    {!x.activo
                      ? "Inactivo"
                      : x.enlazado
                        ? `Enlazado a «${x.titulo_chat ?? "grupo"}»`
                        : "Por enlazar"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              if (nombre.trim()) crear.mutate()
            }}
          >
            <input
              className="campo h-9 min-w-0 flex-1 text-sm"
              placeholder="Nombre, p. ej. «Finanzas»"
              aria-label="Nombre del grupo nuevo"
              maxLength={120}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
            <button
              type="submit"
              className="boton-primario inline-flex shrink-0 items-center gap-2 px-3 py-1.5 text-sm"
              disabled={!nombre.trim() || crear.isPending}
            >
              <Plus size={14} aria-hidden={true} />
              Crear grupo
            </button>
          </form>
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            Nace recibiendo las órdenes de pago; cambia la selección cuando quieras.
          </p>
        </div>

        {/* El detalle del elegido. */}
        {g && (
          <div className="tarjeta space-y-4 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="flex-1 text-base font-semibold">{g.nombre}</h3>
              <span
                className="text-xs"
                style={{ color: g.enlazado ? "var(--success)" : "var(--warning)" }}
              >
                {g.enlazado ? `Enlazado a «${g.titulo_chat ?? "grupo"}»` : "Sin enlazar"}
              </span>
            </div>

            {g.codigo ? (
              <div
                className="rounded-[var(--radius-control)] border border-dashed p-3 text-sm"
                style={{ borderColor: "var(--border)" }}
              >
                {g.enlace && (
                  <a
                    href={g.enlace}
                    target="_blank"
                    rel="noreferrer"
                    className="boton-primario inline-flex items-center gap-2 px-3 py-1.5 text-sm"
                  >
                    <Send size={14} aria-hidden={true} />
                    Elegir el grupo y añadir el bot
                  </a>
                )}
                <p className="mt-2">
                  O añade el bot al grupo y escribe allí:{" "}
                  <code className="tabular-nums font-medium">/vincular {g.codigo}</code>
                </p>
                <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                  ¿Grupo con temas? Escríbelo DENTRO del tema: los avisos irán a ese tema. Para otro
                  tema, crea otro grupo aquí y vincúlalo en el suyo. Telegram no permite elegir
                  quién ve cada tema: todos los miembros los ven todos.
                </p>
                <p className="tabular-nums mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                  Caduca {comoFechaHora(g.codigo_expira_en)}. Esta lista se actualiza sola.
                </p>
              </div>
            ) : (
              <button
                type="button"
                className="boton-secundario inline-flex items-center gap-2 px-3 py-1.5 text-xs"
                disabled={codigo.isPending}
                onClick={() => codigo.mutate(g.id)}
              >
                <RefreshCw size={14} aria-hidden={true} />
                {g.enlazado ? "Mover a otro chat" : "Generar código para enlazar"}
              </button>
            )}

            <div>
              <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                Qué recibe ({g.tipos.length})
              </p>
              <div className="mt-1 space-y-1">
                {areas.map(([area, tipos]) => {
                  const marcados = tipos.filter(([t]) => g.tipos.includes(t)).length
                  return (
                    <details
                      key={area}
                      // Abiertas las áreas que ya reciben algo: lo elegido se ve al entrar.
                      open={marcados > 0}
                      className="rounded-[var(--radius-control)] border px-3 py-2"
                      style={{ borderColor: "var(--border)" }}
                    >
                      <summary className="cursor-pointer text-sm font-medium">
                        {area}{" "}
                        <span
                          className="text-xs font-normal tabular-nums"
                          style={{ color: "var(--fg-muted)" }}
                        >
                          {marcados} de {tipos.length}
                        </span>
                      </summary>
                      <div className="mt-2 grid gap-1 sm:grid-cols-2">
                        {tipos.map(([tipo, etiqueta]) => (
                          <label key={tipo} className="flex min-h-6 items-start gap-2 text-sm">
                            <input
                              type="checkbox"
                              className="mt-1 h-4 w-4 shrink-0"
                              checked={g.tipos.includes(tipo)}
                              disabled={editar.isPending}
                              onChange={(e) =>
                                editar.mutate({
                                  id: g.id,
                                  tipos: e.target.checked
                                    ? [...g.tipos, tipo]
                                    : g.tipos.filter((t) => t !== tipo),
                                })
                              }
                            />
                            {etiqueta}
                          </label>
                        ))}
                      </div>
                    </details>
                  )
                })}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={g.activo}
                  disabled={editar.isPending}
                  onChange={(e) => editar.mutate({ id: g.id, activo: e.target.checked })}
                />
                Activo
              </label>
              <button
                type="button"
                className="boton-secundario ml-auto inline-flex items-center gap-2 px-3 py-1.5 text-xs"
                aria-label={`Eliminar el grupo ${g.nombre}`}
                onClick={async () => {
                  const si = await confirmar({
                    titulo: "Eliminar el grupo",
                    mensaje: `¿Eliminar «${g.nombre}»? El bot deja de escribir en ese grupo.`,
                    aceptar: "Eliminar",
                    peligro: true,
                  })
                  if (si) borrar.mutate(g.id)
                }}
              >
                <Trash2 size={14} aria-hidden={true} />
                Eliminar
              </button>
            </div>
          </div>
        )}
      </div>

      {fallo && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {fallo.message}
        </p>
      )}
      {confirmacion}
    </section>
  )
}
